import { peopleById } from './people';
import { products, productsById, productsBySlug } from './products';
import { homeShowcase } from './showcase';
import { works, worksById, worksBySlug } from './works';

import type { BaseEntry } from './types/entry';
import type { Person } from './types/person';
import type { Product } from './types/product';
import type { ShowcaseItem, ShowcaseKind, ShowcaseLayout } from './types/showcase';
import type { Work } from './types/work';

export const formatEntryDate = (date: string) =>
  new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export const getEntryAuthor = (entry: BaseEntry): Person | undefined => peopleById[entry.authorId];

export const getWorkPath = (slug: string) => `/our-works/${slug}`;
export const getProductPath = (slug: string) => `/our-products/${slug}`;

export const getEntryPath = (kind: ShowcaseKind, slug: string) =>
  kind === 'work' ? getWorkPath(slug) : getProductPath(slug);

export const resolveShowcaseEntry = (item: ShowcaseItem): Work | Product | undefined => {
  if (item.kind === 'work') {
    return worksById[item.id];
  }

  return productsById[item.id];
};

export type ShowcaseCardData = {
  layout: ShowcaseLayout;
  image: string;
  tags: string[];
  title: string;
  description: string;
  date: string;
  articleLink: string;
  author: {
    name: string;
    role: string;
    image?: string;
  };
};

export const toShowcaseCardData = (
  entry: BaseEntry,
  layout: ShowcaseLayout,
  kind: ShowcaseKind,
): ShowcaseCardData => {
  const author = getEntryAuthor(entry);

  return {
    layout,
    image: entry.image,
    tags: entry.tags,
    title: entry.title,
    description: entry.description,
    date: formatEntryDate(entry.date),
    articleLink: getEntryPath(kind, entry.slug),
    author: {
      name: author?.name ?? '',
      role: author?.role ?? '',
      image: author?.avatar ?? author?.image,
    },
  };
};

export const featuredShowcaseItem = homeShowcase.find((item) => item.layout === 'featured') ?? homeShowcase[0];

export const compactShowcaseItems = homeShowcase.filter((item) => item !== featuredShowcaseItem);

export { homeShowcase, peopleById, products, productsById, productsBySlug, works, worksById, worksBySlug };
